import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sprout, HeartHandshake, UtensilsCrossed, Leaf, Users, HandHeart, Sparkles, Sun } from "lucide-react";
import { Reveal, MaskedLine, MaskedViewLine } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { BeeMark } from "../components/BeeLogo";
import { CTABand, SectionHead } from "../components/Shared";
import { LOGO_URL, GIVING_URL, GARDEN_REAL, REAL_PHOTOS, IMAGES, PILLARS, YEARS } from "../lib/constants";
import { useTitle } from "../lib/useTitle";

const PILLAR_ICONS = [Sparkles, HeartHandshake, UtensilsCrossed, Leaf, Users, HandHeart];

const JOURNEY_STYLES = {
  wing: { badge: "bg-wing text-white", ring: "hover:border-wing/40", num: "text-wing" },
  garden: { badge: "bg-garden text-white", ring: "hover:border-garden/40", num: "text-garden" },
  pollen: { badge: "bg-pollen text-charcoal", ring: "hover:border-pollen/60", num: "text-pollen-dark" },
};

const STRIP_PHOTOS = [
  { src: REAL_PHOTOS.buildBeds, caption: "Raised beds rising at our garden home", alt: "Newly built raised garden beds outside our community building" },
  { src: REAL_PHOTOS.meal, caption: "Community meal night, plates full", alt: "A long table covered in a red cloth, laden with food as community members gather to eat" },
  { src: REAL_PHOTOS.circle, caption: "Sisters standing shoulder to shoulder", alt: "Women of the program standing in a circle with arms linked, wearing Bee Courageous shirts" },
];

export default function Home() {
  useTitle("Bee Courageous @ Garden Grub | Healing Takes Root");
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 600], [0, -70]);
  const beeY = useTransform(scrollY, [0, 600], [0, 50]);

  return (
    <>
      {/* Kinetic hero */}
      <section className="grain honeycomb-bg relative overflow-hidden pt-28 lg:pt-36" data-testid="home-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-16 sm:px-6 lg:grid-cols-12 lg:px-12 lg:pb-24">
          <div className="lg:col-span-6">
            <motion.span
              className="eyebrow"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              data-testid="hero-eyebrow"
            >
              A faith-based healing program for women
            </motion.span>
            <h1 className="font-serif text-4xl font-semibold leading-[1.06] tracking-tight text-charcoal sm:text-5xl lg:text-6xl xl:text-7xl">
              <MaskedLine delay={0.1}>Healing takes root.</MaskedLine>
              <MaskedLine delay={0.28} className="italic text-wing">Courage learns to fly.</MaskedLine>
            </h1>
            <Reveal delay={0.5}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-charcoal/70 sm:text-lg">
                Bee Courageous @ Garden Grub walks with women who have known trauma, loss, and instability — through faith,
                nourishment, gardening, vocational training, and a three-year journey from survival to self-sufficiency.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/program" className="btn-primary" data-testid="hero-program-btn">
                  Explore the Three-Year Journey <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <Link to="/get-involved" className="btn-outline" data-testid="hero-involved-btn">
                  Get Involved
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-xs font-bold uppercase tracking-[0.18em] text-charcoal/50">
                <span className="flex items-center gap-2"><Sun className="h-4 w-4 text-pollen" aria-hidden="true" /> Faith-rooted</span>
                <span className="flex items-center gap-2"><Sprout className="h-4 w-4 text-garden" aria-hidden="true" /> Garden-grown</span>
                <span className="flex items-center gap-2"><Users className="h-4 w-4 text-wing" aria-hidden="true" /> Community-held</span>
              </div>
            </Reveal>
          </div>

          <div className="relative self-center lg:col-span-6">
            <motion.div
              style={{ y: imgY }}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-[2.5rem] border-4 border-white shadow-2xl shadow-wing/20"
            >
              <img
                src="/images/women-of-the-hive.jpg"
                alt="Illustrated banner of the women of Bee Courageous embracing beneath a garden sign reading Nourish and Restore"
                className="w-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-wing/15 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              style={{ y: beeY }}
              className="absolute -left-4 -top-8 hidden rounded-3xl border border-white/60 bg-white/85 p-4 shadow-xl shadow-wing/15 backdrop-blur-xl sm:block"
            >
              <img src={LOGO_URL} alt="" className="h-20 w-20 animate-float-slow rounded-full object-cover" />
            </motion.div>
            <Reveal delay={0.7} className="absolute -bottom-6 -right-3 sm:-right-6">
              <div className="rounded-3xl border border-white/60 bg-white/90 px-6 py-4 shadow-xl shadow-garden/15 backdrop-blur-xl">
                <p className="font-serif text-3xl font-semibold text-garden">3 Years</p>
                <p className="text-xs font-bold uppercase tracking-widest text-charcoal/60">to restoration & leadership</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />

      {/* Verse banner — original design, brand fonts */}
      <section className="relative overflow-hidden bg-wing-dark" data-testid="verse-banner">
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.08 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
        >
          <img src={IMAGES.lavender} alt="" aria-hidden="true" className="h-full w-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-wing-dark/90 via-wing/70 to-garden-dark/60" />
        </motion.div>
        <div className="relative mx-auto max-w-4xl px-4 py-24 text-center sm:px-6 lg:py-36">
          <span className="eyebrow !bg-white/10 !text-pollen" data-testid="verse-eyebrow">Our Anchor Verse</span>
          <h2 className="font-serif text-4xl font-semibold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedViewLine delay={0.1}>Be strong and of a good courage;</MaskedViewLine>
            <MaskedViewLine delay={0.26} className="italic text-pollen">be not afraid,</MaskedViewLine>
            <MaskedViewLine delay={0.42}>for the Lord thy God is with thee</MaskedViewLine>
            <MaskedViewLine delay={0.58}>whithersoever thou goest.</MaskedViewLine>
          </h2>
          <Reveal delay={0.75}>
            <p className="mt-8 text-xs font-bold uppercase tracking-[0.35em] text-white/80" data-testid="verse-citation">Joshua 1:9</p>
            <BeeMark className="mx-auto mt-6 h-10 w-10 text-white/70" />
          </Reveal>
        </div>
      </section>

      {/* Six pillars bento */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28" data-testid="pillars-section">
        <SectionHead
          eyebrow="How We Heal Together"
          title="Six roots of restoration"
          text="Every woman in the program is nurtured through six intertwining roots — spiritual, physical, relational, and practical."
        />
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-8 lg:grid-cols-12 lg:gap-8">
          {PILLARS.map((p, i) => {
            const Icon = PILLAR_ICONS[i];
            const spans = [
              "md:col-span-4 lg:col-span-7",
              "md:col-span-4 lg:col-span-5",
              "md:col-span-4 lg:col-span-5",
              "md:col-span-4 lg:col-span-7",
              "md:col-span-4 lg:col-span-6",
              "md:col-span-4 lg:col-span-6",
            ];
            return (
              <Reveal key={p.title} delay={0.08 * i} className={spans[i]}>
                <article className="card-soft h-full" data-testid={`pillar-${i}`}>
                  <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-wing-soft text-wing">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h3 className="font-serif text-xl font-semibold text-charcoal sm:text-2xl">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/70 sm:text-base">{p.text}</p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Three-year journey */}
      <section className="bg-garden-soft/60 py-20 lg:py-28" data-testid="journey-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <SectionHead
            center
            eyebrow="The Three-Year Program"
            title="From survival to self-sufficiency"
            text="A deliberate, unhurried progression — because real restoration grows season by season."
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-3 lg:gap-8">
            {YEARS.map((y, i) => {
              const s = JOURNEY_STYLES[y.color];
              return (
                <Reveal key={y.num} delay={0.12 * i}>
                  <Link
                    to="/program"
                    data-testid={`journey-year-${i + 1}`}
                    className={`card-soft group flex h-full flex-col border-2 border-transparent ${s.ring}`}
                  >
                    <span className={`w-fit rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] ${s.badge}`}>{y.num}</span>
                    <h3 className={`mt-5 font-serif text-2xl font-semibold leading-snug ${s.num}`}>{y.name}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-charcoal/70">{y.text}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-wing transition-transform duration-300 group-hover:translate-x-1">
                      See the full year <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Straight from our community — real photos */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28" data-testid="community-strip">
        <SectionHead
          center
          eyebrow="Straight From Our Garden"
          title="Real moments, real sisterhood"
          text="Glimpses from our beds, our tables, and our circle — no stock photos here."
        />
        <div className="mt-12 flex flex-wrap items-start justify-center gap-6 sm:gap-8 lg:gap-12">
          {STRIP_PHOTOS.map((p, i) => (
            <Reveal key={p.caption} delay={0.1 * i}>
              <figure
                className={`w-44 rounded-2xl bg-white p-3 pb-4 shadow-xl shadow-garden/15 transition-transform duration-300 hover:-translate-y-2 hover:rotate-0 sm:w-56 ${i % 2 === 0 ? "-rotate-2" : "rotate-2"}`}
                data-testid={`community-photo-${i}`}
              >
                <img src={p.src} alt={p.alt} className="aspect-square w-full rounded-xl object-cover" loading="lazy" />
                <figcaption className="mt-3 text-center font-serif text-sm italic text-charcoal/70">{p.caption}</figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Image + quote band */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-12 lg:pb-28">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="arch-frame overflow-hidden border-4 border-white shadow-2xl shadow-garden/15">
              <img src={GARDEN_REAL} alt="One of our actual garden beds, blooming with marigolds, pinks, and purple asters" className="aspect-[4/5] w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <BeeMark className="mb-6 h-14 w-14 text-charcoal" />
              <blockquote className="font-serif text-2xl font-medium italic leading-snug text-charcoal sm:text-3xl lg:text-4xl">
                "The bee does not rush the bloom. Here, every woman is given the time, the soil, and the sisterhood she
                needs to become who God always intended."
              </blockquote>
              <p className="mt-6 text-sm font-bold uppercase tracking-[0.2em] text-wing">Our promise to every woman</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/about" className="btn-outline" data-testid="quote-about-btn">Our Story</Link>
                <a href={GIVING_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="quote-donate-btn">
                  Sow a Seed of Hope
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand
        title="Every woman deserves a place to heal, grow, and lead."
        text="Whether you are seeking healing, offering your hands, or giving — there is a place for you in this garden."
      />
    </>
  );
}
